import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback } from 'react';
import { dataProvider } from '@/services/data-provider';
import { whatsappApiClient, messagingApiClient } from '@/services/api-client';
import { v4 as uuidv4 } from 'uuid';
import { Channel } from '@/features/channels/channels.types';
import {
  WhatsAppError,
  WhatsAppSessionStatusResponse,
  CreateWhatsAppSessionRequest,
  WhatsAppSessionResponse,
} from '../../services/interface';
import {
  createChannel,
  createChannelPhoneNumber,
  fetchChannel,
  getChannelPhoneNumbersList,
  getChannelsList,
  updateChannel,
  updateChannelPhoneNumber,
} from '@/features/channels/channels.service';
import { OrgLocation } from '@/features/organizations/organizations.types';
import { persistStorageService } from '@/features/auth/persist-storage';

/**
 * Hook for managing WhatsApp channels and sessions
 */
export function useWhatsAppChannel() {
  const queryClient = useQueryClient();

  // == REAL API CALLS ==

  // Fetch QR code from the WhatsApp server
  async function fetchQRCode(
    sessionName: string,
    format: 'image' | 'base64' = 'image',
  ): Promise<string> {
    try {
      if (format === 'base64') {
        // Get QR code as base64
        const response = await whatsappApiClient.get(
          `/api/${sessionName}/auth/qr?format=image`,
        );

        // Return the base64 data directly
        return `data:${response.data.mimetype};base64,${response.data.data}`;
      } else {
        // Get QR code as binary image
        const response = await whatsappApiClient.get(
          `/api/${sessionName}/auth/qr?format=image`,
          {
            responseType: 'blob',
          },
        );

        // Convert blob to object URL
        const blob = new Blob([response.data], { type: 'image/png' });
        return URL.createObjectURL(blob);
      }
    } catch (error) {
      console.error('Error fetching QR code:', error);
      throw new Error(
        `Failed to fetch QR code: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  // Fetch session status from the WhatsApp server
  async function fetchSessionStatus(
    sessionName: string,
  ): Promise<WhatsAppSessionStatusResponse> {
    try {
      const response =
        await whatsappApiClient.get<WhatsAppSessionStatusResponse>(
          `/api/sessions/${sessionName}`,
        );

      return response.data;
    } catch (error) {
      console.error('Error fetching session status:', error);
      throw new Error(
        `Failed to fetch session status: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  // Create a new WhatsApp session
  async function createWASession(
    sessionName: string,
    metadata: Record<string, string> = {},
  ): Promise<WhatsAppSessionResponse> {
    try {
      const payload: CreateWhatsAppSessionRequest = {
        name: sessionName,
        start: true,
        config: {
          metadata: {
            ...metadata,
            'created.timestamp': new Date().toISOString(),
          } as any,
          proxy: null,
          debug: false,
        },
        webhooks: [
          {
            // Update this URL to your actual webhook endpoint
            url: `${import.meta.env.VITE_CHANNEL_WEBHOOK_URL}`,
            events: ['message.any', 'session.status'],
            retries: 15,
          },
        ],
      };

      const response = await whatsappApiClient.post<WhatsAppSessionResponse>(
        '/api/sessions',
        payload,
      );

      return response.data;
    } catch (error) {
      console.error('Error creating WhatsApp session:', error);
      throw new Error(
        `Failed to create WhatsApp session: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  // Start an existing session
  async function startWASession(sessionName: string): Promise<void> {
    try {
      await whatsappApiClient.post(`/api/sessions/${sessionName}/start`, {});
    } catch (error) {
      console.error('Error starting WhatsApp session:', error);
      throw new Error(
        `Failed to start WhatsApp session: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  // Stop an existing session
  async function stopWASession(sessionName: string): Promise<void> {
    try {
      await whatsappApiClient.post(`/api/sessions/${sessionName}/stop`, {});
    } catch (error) {
      console.error('Error stopping WhatsApp session:', error);
      throw new Error(
        `Failed to stop WhatsApp session: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  // Add a new function to create just a WhatsApp session without DB channel
  async function createTemporarySession(): Promise<string> {
    try {
      const sessionId = uuidv4();
      await createWASession(sessionId, {
        'temp.session': 'true',
        'created.timestamp': new Date().toISOString(),
      });
      return sessionId;
    } catch (error) {
      console.error('Error creating temporary session:', error);
      throw error;
    }
  }

  // Update a session with new config
  async function updateWASession(
    sessionName: string,
    config: any,
  ): Promise<void> {
    try {
      await whatsappApiClient.put(`/api/sessions/${sessionName}`, {
        name: sessionName,
        config,
      });
    } catch (error) {
      console.error('Error updating WhatsApp session:', error);
      throw new Error(
        `Failed to update WhatsApp session: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  // Restart an existing session
  async function restartWASession(sessionName: string): Promise<void> {
    try {
      await whatsappApiClient.post(`/api/sessions/${sessionName}/restart`, {});
    } catch (error) {
      console.error('Error restarting WhatsApp session:', error);
      throw new Error(
        `Failed to restart WhatsApp session: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  // Logout from session
  async function logoutWASession(sessionName: string): Promise<void> {
    try {
      await whatsappApiClient.post(`/api/sessions/${sessionName}/logout`, {});
    } catch (error) {
      console.error('Error logging out WhatsApp session:', error);
      throw new Error(
        `Failed to logout WhatsApp session: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  // Delete a session completely
  async function deleteWASession(sessionName: string): Promise<void> {
    try {
      await whatsappApiClient.delete(`/api/sessions/${sessionName}`);
    } catch (error) {
      console.error('Error deleting WhatsApp session:', error);
      throw new Error(
        `Failed to delete WhatsApp session: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  // Fetch all sessions from the WhatsApp server
  async function fetchAllSessions(
    includeStoppedSessions: boolean = true,
  ): Promise<WhatsAppSessionResponse[]> {
    try {
      const response = await whatsappApiClient.get<WhatsAppSessionResponse[]>(
        `/api/sessions${includeStoppedSessions ? '?all=true' : ''}`,
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching all sessions:', error);
      throw new Error(
        `Failed to fetch sessions: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  // == MUTATIONS ==

  // Create Channel Mutation
  const createChannelMutation = useMutation<
    Channel,
    Error,
    {
      sessionName: string;
      channelName: string;
      phoneNumber: string;
      locationId: string;
      organisationId: string;
    }
  >({
    mutationFn: async ({
      sessionName,
      channelName,
      phoneNumber,
      locationId,
      organisationId,
    }) => {
      try {
        // Step 1: Create channel record using the existing connected session
        // and use the sessionName as the channel ID
        const { data: channel } = await createChannel({
          id: sessionName, // Use sessionName as the ID
          name: channelName,
          type: 'whatsapp',
          status: 'WORKING',
          location_id: locationId || '',
          organisation_id: organisationId || '',
          channel_config: {
            sessionId: sessionName,
            businessName: channelName,
            businessPhone: phoneNumber,
            whatsapp: {
              sessionName: sessionName,
              connectedAt: new Date().toISOString(),
            },
          },
        });

        console.log('Channel created successfully:', channel);

        // Step 2: Create phone number record
        try {
          const phoneNumberData = {
            channel_id: sessionName, // Use sessionName here too
            phone_number: phoneNumber,
            is_active: true,
            provider: 'whatsapp',
            phone_settings: {},
          };

          console.log(
            'Creating channel phone number with data:',
            phoneNumberData,
          );

          const { data: phoneRecord } =
            await createChannelPhoneNumber(phoneNumberData);

          console.log(
            'Channel phone number created successfully:',
            phoneRecord,
          );

          // Return the channel with the phone number added
          return {
            ...channel,
            phone: phoneNumber,
          } as Channel;
        } catch (err) {
          console.error('Error creating channel phone number record:', err);
          return channel as Channel;
        }
      } catch (error) {
        console.error('Error in channel creation flow:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      queryClient.invalidateQueries({ queryKey: ['channel-phone-numbers'] });
    },
    onError: (error) => {
      console.error('Failed to create channel:', error);
    },
  });

  // Create a new mutation for creating a temporary session
  const createTempSessionMutation = useMutation<string, Error, void>({
    mutationFn: createTemporarySession,
    onSuccess: (sessionId) => {
      console.log('Temporary session created:', sessionId);
    },
    onError: (error) => {
      console.error('Failed to create temporary session:', error);
    },
  });

  // Update channel information (like phone number, after connection)
  const updateChannelMutation = useMutation<
    Channel,
    Error,
    { id: string; data: Partial<Channel> }
  >({
    mutationFn: async ({ id, data }) => {
      // If channel_config is provided as a string, parse it to an object
      const updatedData = { ...data };
      if (typeof updatedData.channel_config === 'string') {
        try {
          updatedData.channel_config = JSON.parse(updatedData.channel_config);
        } catch (e) {
          console.error('Error parsing channel_config string:', e);
        }
      }

      const response = await updateChannel({
        id,
        data: updatedData,
        previousData: data,
      });

      // If there's a phone number, update or create a channel_phone_number record
      if (updatedData.phone) {
        try {
          // First check if a record exists
          const { data: phoneData } = await getChannelPhoneNumbersList({
            filter: { channel_id: id },
            page: 1,
            perPage: 1,
            sort: 'id',
            order: 'DESC',
          });

          if (phoneData && phoneData.length > 0) {
            // Update existing record
            await updateChannelPhoneNumber({
              id: phoneData[0].id,
              data: {
                phone_number: updatedData.phone,
                is_active: true,
              },
              previousData: phoneData[0],
            });
          } else {
            // Create new record
            await createChannelPhoneNumber({
              channel_id: id,
              phone_number: updatedData.phone,
              is_active: true,
              provider: 'whatsapp',
              phone_settings: {},
            });
          }
        } catch (err) {
          console.warn('Error updating phone number record:', err);
          // Don't fail the whole process if this step fails
        }
      }

      return response.data as Channel;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      queryClient.invalidateQueries({ queryKey: ['channel_phone_numbers'] });
      console.log('Channel updated successfully:', data);
    },
    onError: (error) => {
      console.error('Failed to update channel:', error);
    },
  });

  // Start Session Mutation
  const startSessionMutation = useMutation<void, Error, string>({
    mutationFn: async (sessionName: string) => {
      await startWASession(sessionName);

      // Wait a moment for the server to process the request
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Check the status right away
      try {
        const status = await fetchSessionStatus(sessionName);

        // Update the channel status in the database
        const { data: channel } = await fetchChannel({ id: sessionName });

        if (channel && channel.status !== status.status) {
          console.log(
            `Updating channel ${sessionName} status: ${channel.status} → ${status.status}`,
          );

          await updateChannel({
            id: sessionName,
            data: { status: status.status },
            previousData: channel as Channel,
          });
        }
      } catch (err) {
        console.warn(
          'Failed to fetch updated status after starting session:',
          err,
        );
        // Don't throw here, the main operation succeeded
      }
    },
    onSuccess: (_, sessionName) => {
      queryClient.invalidateQueries({
        queryKey: ['whatsappSessionStatus', sessionName],
      });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
    onError: (error, sessionName) => {
      console.error(`Failed to start session ${sessionName}:`, error);
    },
  });

  // Stop Session Mutation
  const stopSessionMutation = useMutation<void, Error, string>({
    mutationFn: async (sessionName: string) => {
      await stopWASession(sessionName);

      // Wait a moment for the server to process the request
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Check the status right away
      try {
        const status = await fetchSessionStatus(sessionName);

        // Update the channel status in the database
        const { data: channel } = await fetchChannel({ id: sessionName });

        if (channel && channel.status !== status.status) {
          console.log(
            `Updating channel ${sessionName} status: ${channel.status} → ${status.status}`,
          );

          await updateChannel({
            id: sessionName,
            data: { status: status.status },
            previousData: channel,
          });
        }
      } catch (err) {
        console.warn(
          'Failed to fetch updated status after stopping session:',
          err,
        );
        // Don't throw here, the main operation succeeded
      }
    },
    onSuccess: (_, sessionName) => {
      queryClient.invalidateQueries({
        queryKey: ['whatsappSessionStatus', sessionName],
      });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
    onError: (error, sessionName) => {
      console.error(`Failed to stop session ${sessionName}:`, error);
    },
  });

  // Update Session Mutation
  const updateSessionMutation = useMutation<
    void,
    Error,
    { sessionName: string; config: Record<string, unknown> }
  >({
    mutationFn: ({ sessionName, config }) =>
      updateWASession(sessionName, config),
    onSuccess: (_, { sessionName }) => {
      queryClient.invalidateQueries({
        queryKey: ['whatsappSessionStatus', sessionName],
      });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
    onError: (error, { sessionName }) => {
      console.error(`Failed to update session ${sessionName}:`, error);
    },
  });

  // Restart Session Mutation
  const restartSessionMutation = useMutation<void, Error, string>({
    mutationFn: restartWASession,
    onSuccess: (_, sessionName) => {
      queryClient.invalidateQueries({
        queryKey: ['whatsappSessionStatus', sessionName],
      });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
    onError: (error, sessionName) => {
      console.error(`Failed to restart session ${sessionName}:`, error);
    },
  });

  // Logout Session Mutation
  const logoutSessionMutation = useMutation<void, Error, string>({
    mutationFn: logoutWASession,
    onSuccess: (_, sessionName) => {
      queryClient.invalidateQueries({
        queryKey: ['whatsappSessionStatus', sessionName],
      });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
    onError: (error, sessionName) => {
      console.error(`Failed to logout session ${sessionName}:`, error);
    },
  });

  // Delete Session Mutation
  const deleteSessionMutation = useMutation<void, Error, string>({
    mutationFn: deleteWASession,
    onSuccess: (_, sessionName) => {
      queryClient.invalidateQueries({
        queryKey: ['whatsappSessionStatus', sessionName],
      });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
    onError: (error, sessionName) => {
      console.error(`Failed to delete session ${sessionName}:`, error);
    },
  });

  // Add a new mutation for syncing session status with database
  const syncSessionStatusMutation = useMutation<void, Error, string>({
    mutationFn: async (channelId: string) => {
      try {
        // 1. Get all sessions from WhatsApp server
        const sessions = await fetchAllSessions(true);

        // 2. Find the matching session by ID/name
        const session = sessions.find((s) => s.name === channelId);

        if (!session) {
          console.warn(`Session ${channelId} not found on WhatsApp server`);
          return;
        }

        // 3. Get current channel from database
        const { data: channel } = await fetchChannel({ id: channelId });

        // 4. Update if status is different
        if (channel && channel.status !== session.status) {
          console.log(
            `Syncing channel ${channelId} status: ${channel.status} → ${session.status}`,
          );

          await updateChannel({
            id: channelId,
            data: {
              status: session.status,
            },
            previousData: channel,
          });
        }
      } catch (error) {
        console.error('Error syncing session status:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });

  // Sync all session statuses using backend sync endpoint
  // This calls the agents-messaging server which fetches from WAHA and updates DB
  const syncAllSessionStatusesMutation = useMutation<
    {
      success: boolean;
      summary?: {
        waha_sessions: number;
        db_channels: number;
        updated: number;
        orphaned_in_db: number;
        not_in_db: number;
      };
      updates?: Array<{
        channel_id: string;
        old_status: string;
        new_status: string;
      }>;
      orphaned_in_db?: Array<{
        channel_id: string;
        name: string;
        db_status: string;
      }>;
      not_in_db?: Array<{
        session_name: string;
        waha_status: string;
      }>;
      error?: string;
    },
    Error,
    void
  >({
    mutationFn: async () => {
      try {
        // Prevent multiple calls by adding a timestamp check
        const now = Date.now();
        const lastSyncTime =
          (queryClient.getQueryData(['lastStatusSyncTime']) as number) || 0;

        // Only sync if more than 30 seconds has passed since last sync
        if (now - lastSyncTime < 30000) {
          console.log(
            'Skipping status sync - last sync was less than 30 seconds ago',
          );
          return { success: true, summary: undefined };
        }

        queryClient.setQueryData(['lastStatusSyncTime'], now);

        // Call backend sync endpoint
        const response = await messagingApiClient.post('/sync_waha_channels');
        const result = response.data;

        if (result.success) {
          console.log('WAHA sync completed:', result.summary);
          if (result.updates && result.updates.length > 0) {
            console.log('Updated channels:', result.updates);
          }
          if (result.orphaned_in_db && result.orphaned_in_db.length > 0) {
            console.warn(
              'Channels in DB but not in WAHA:',
              result.orphaned_in_db,
            );
          }
          if (result.not_in_db && result.not_in_db.length > 0) {
            console.warn(
              'WAHA sessions not in DB:',
              result.not_in_db,
            );
          }
        } else {
          console.error('WAHA sync failed:', result.error);
        }

        return result;
      } catch (error) {
        console.error('Error syncing all session statuses:', error);
        throw error;
      }
    },
    onSuccess: (result) => {
      // Invalidate channels query to refresh the UI
      if (result.success && result.summary && result.summary.updated > 0) {
        queryClient.invalidateQueries({
          queryKey: ['channels'],
        });
      }
    },
  });

  // == QUERIES ==

  // Get QR Code Query
  function useWhatsAppQRCode(
    sessionName: string | undefined,
    needsQrCode: boolean = false,
  ) {
    const [revokeList, setRevokeList] = useState<string[]>([]);

    // Add cleanup for any created object URLs to prevent memory leaks
    useEffect(() => {
      return () => {
        revokeList.forEach((url) => URL.revokeObjectURL(url));
      };
    }, [revokeList]);

    // First check if we need a QR code by checking session status
    const { data: sessionStatus } = useQuery<
      WhatsAppSessionStatusResponse,
      WhatsAppError
    >({
      queryKey: ['whatsappSessionStatus', sessionName],
      queryFn: () => fetchSessionStatus(sessionName!),
      enabled: !!sessionName,
      // Only run this check every 5 seconds to avoid excessive API calls
      refetchInterval: 5000,
      retry: 1,
    });

    // Determine if we need QR based on actual status
    const requiresQR = sessionStatus?.status === 'SCAN_QR_CODE';

    const query = useQuery<string, WhatsAppError>({
      queryKey: ['whatsappQR', sessionName],
      queryFn: () => fetchQRCode(sessionName!, 'base64'),
      enabled: !!sessionName && (needsQrCode || requiresQR),
      refetchInterval: needsQrCode || requiresQR ? 8000 : false,
      retry: 3,
      staleTime: 7000,
      gcTime: 60000 * 5,
    });

    // Track successful URLs for cleanup
    useEffect(() => {
      if (query.data) {
        setRevokeList((prev) => [...prev, query.data]);
      }
    }, [query.data]);

    // Enhanced refresh function that first checks if status is really SCAN_QR_CODE
    const safeRefetch = useCallback(async () => {
      if (!sessionName) return null;

      try {
        // First check if we still need a QR code
        const status = await fetchSessionStatus(sessionName);
        if (status.status !== 'SCAN_QR_CODE') {
          console.log(
            `Not refreshing QR - status is ${status.status}, not SCAN_QR_CODE`,
          );
          return null;
        }

        // Revoke any existing URL before refetching
        if (query.data) {
          URL.revokeObjectURL(query.data as string);
        }
        return query.refetch();
      } catch (err) {
        console.error(
          'Error checking session status before refreshing QR:',
          err,
        );
        return query.refetch(); // Attempt to fetch QR anyway
      }
    }, [sessionName, query]);

    return {
      ...query,
      refetch: safeRefetch,
      needsQrCode: needsQrCode || requiresQR,
    };
  }

  // Get Session Status Query
  function useWhatsAppSessionStatus(sessionName: string | undefined) {
    return useQuery<WhatsAppSessionStatusResponse, WhatsAppError>({
      queryKey: ['whatsappSessionStatus', sessionName],
      queryFn: () => fetchSessionStatus(sessionName!),
      enabled: !!sessionName,
      refetchInterval: (query) => {
        const data = query.state.data as
          | WhatsAppSessionStatusResponse
          | undefined;
        // Adjust polling based on status
        switch (data?.status) {
          case 'SCAN_QR_CODE':
            return 8000; // 8s - more frequent for QR code state
          case 'PENDING':
          case 'STARTING':
          case 'INITIALIZING':
            return 3000; // 3s - more frequent for transition states
          case 'WORKING':
            return 30000; // 30s for stable state
          case 'FAILED':
            return 10000; // 10s - check failed state reasonably often
          default:
            return 15000; // 15s for others (STOPPED etc.)
        }
      },
      retry: 3,
    });
  }

  // Fetch WhatsApp channels
  const useWhatsAppChannels = () => {
    return useQuery({
      queryKey: ['channels-with-phones', { type: 'whatsapp' }],
      queryFn: async () => {
        // 1. Fetch WhatsApp channels
        const channelsResponse = await getChannelsList({
          filter: { type: 'whatsapp' },
          page: 1,
          perPage: 100,
          sort: 'name',
          order: 'ASC',
        });

        const channels = channelsResponse.data || [];

        if (channels.length === 0) return [];

        // 2. Fetch phone numbers for these channels
        const channelIds = channels.map((channel) => channel.id);
        try {
          const { data: phoneNumbersResponse } =
            await getChannelPhoneNumbersList({
              filter: { channel_id: channelIds },
              page: 1,
              perPage: 100,
              sort: 'id',
              order: 'ASC',
            });

          const phoneNumbersMap = (phoneNumbersResponse || []).reduce(
            (map, phoneRecord) => {
              map[phoneRecord.channel_id as string] = phoneRecord.phone_number;
              return map;
            },
            {} as Record<string, string>,
          );

          // 3. Merge channel data with phone numbers
          return channels.map((channel) => ({
            ...channel,
            phone: phoneNumbersMap[channel.id] || channel.phone || '-',
          }));
        } catch (error) {
          console.error('Error fetching phone numbers:', error);
          // If phone number fetch fails, return channels without phone numbers
          return channels;
        }
      },
      staleTime: 60 * 1000, // Cache for 1 minute
    });
  };

  // Add this function to the useWhatsAppChannel hook
  function useWhatsAppChannelsWithPhones() {
    return useQuery({
      queryKey: ['whatsapp-channels-with-phones'],
      queryFn: async () => {
        // 1. Fetch WhatsApp channels
        const channelsResponse = await getChannelsList({
          filter: { type: 'whatsapp' },
          page: 1,
          perPage: 100,
          sort: 'name',
          order: 'ASC',
        });

        const channels = channelsResponse.data || [];

        if (channels.length === 0) return [];

        // 2. Fetch phone numbers for these channels
        const channelIds = channels.map((channel) => channel.id);
        const { data: phoneNumbersResponse } = await getChannelPhoneNumbersList(
          {
            filter: { channel_id: channelIds },
            page: 1,
            perPage: 100,
            sort: 'id',
            order: 'ASC',
          },
        );

        const phoneNumbersMap = (phoneNumbersResponse || []).reduce(
          (map, phoneRecord) => {
            map[phoneRecord.channel_id] = phoneRecord.phone_number;
            return map;
          },
          {} as Record<string, string>,
        );

        // 3. Merge channel data with phone numbers
        return channels.map((channel) => ({
          ...channel,
          phone: phoneNumbersMap[channel.id] || channel.phone || '-',
        }));
      },
      staleTime: 60 * 1000, // Cache for 1 minute
      refetchOnWindowFocus: false,
    });
  }

  // Add this function to useWhatsAppChannel
  function useChatbots() {
    return useQuery({
      queryKey: ['chatbots-for-conversation'],
      queryFn: async () => {
        try {
          // Get the selected org and location from localStorage
          const selectedOrg = persistStorageService.getPersistItem<OrgLocation>(
            {
              name: 'selectedOrganisation',
              parse: true,
            },
          );

          const selectedLoc = persistStorageService.getPersistItem<OrgLocation>(
            {
              name: 'selectedLocation',
              parse: true,
            },
          );

          // Fetch chatbots filtered by org and location
          const response = await dataProvider.getList('chatbots', {
            filter: {
              organisation_id: selectedOrg?.id || '',
              location_id: selectedLoc?.id || '',
            },
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
          });

          return response.data || [];
        } catch (error) {
          console.error('Error fetching chatbots:', error);
          return [];
        }
      },
      staleTime: 60 * 1000, // Cache for 1 minute
    });
  }

  return {
    // New temporary session functionality
    createTemporarySession: createTempSessionMutation.mutateAsync,
    isCreatingTempSession: createTempSessionMutation.isPending,

    // Modified create channel
    createChannel: createChannelMutation.mutateAsync,
    updateChannel: updateChannelMutation.mutateAsync,
    isCreatingChannel: createChannelMutation.isPending,
    isUpdatingChannel: updateChannelMutation.isPending,

    // Session management
    startSession: startSessionMutation.mutateAsync,
    stopSession: stopSessionMutation.mutateAsync,
    isStartingSession: startSessionMutation.isPending,
    isStoppingSession: stopSessionMutation.isPending,

    // New session management
    updateSession: updateSessionMutation.mutateAsync,
    restartSession: restartSessionMutation.mutateAsync,
    logoutSession: logoutSessionMutation.mutateAsync,
    deleteSession: deleteSessionMutation.mutateAsync,
    isUpdatingSession: updateSessionMutation.isPending,
    isRestartingSession: restartSessionMutation.isPending,
    isLoggingOutSession: logoutSessionMutation.isPending,
    isDeletingSession: deleteSessionMutation.isPending,

    // Queries
    useWhatsAppQRCode,
    useWhatsAppSessionStatus,

    // Add these new functions
    syncSessionStatus: syncSessionStatusMutation.mutateAsync,
    syncAllSessionStatuses: syncAllSessionStatusesMutation.mutateAsync,
    isSyncingStatus:
      syncSessionStatusMutation.isPending ||
      syncAllSessionStatusesMutation.isPending,
    useWhatsAppChannels,
    useWhatsAppChannelsWithPhones,
    useChatbots,
  };
}
